/**
 * City finder control component and autocomplete control.
 *
 * Implements IControl:
 * https://docs.mapbox.com/mapbox-gl-js/api/markers/#icontrol
 */
import {CityService} from "../common/CityService";
import MapService from "../common/MapService";
import type { CityRecord } from "../storage/CityStorage";
import type { IControl, Map } from 'mapbox-gl';

//import styles from './CityFinderControl.scss';

class CityFinderControl implements IControl {
    _map: Map | undefined;
    /** Clase de estilo activa. Se asigna en onAdd. */
    mapStyle: string | undefined;
    _container = document.createElement('div');
    _button = document.createElement('button');
    _finderBackground = document.createElement('div');
    _finderContainer = document.createElement('div');
    _input = document.createElement('input');
    _list = document.createElement('ul');
    CLASS_STANDARD = 'standard';
    CLASS_BUTTON_CITY_FINDER = 'button_city_finder';
    CLASS_BUTTON_CONTROL = 'mapboxgl-ctrl';
    CLASS_INPUT_VISIBLE = 'finder_input_visible';
    visible = false;

    resetComponent(): void {
        this._list.innerHTML = '';
    }

    handleButtonCityFinder(event?: MouseEvent): void {
        // Prevent event propagation to avoid closing the card when opening the finder
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.resetComponent();
        this._input.value = '';
        this._finderBackground.classList.toggle(this.CLASS_INPUT_VISIBLE);
        this._container.classList.toggle(this.CLASS_INPUT_VISIBLE);
        if (this._container.classList.contains(this.CLASS_INPUT_VISIBLE)) {
            this._input.focus();
        }
    }

    async handleInputChange(): Promise<void> {
        const cityName = this._input.value.trim();
        if (cityName.length >= 3) {
            const cityList = await CityService.findCity(cityName);
            this.resetComponent();
            cityList.forEach(cityData => {
                const city = document.createElement('li');
                city.innerHTML = cityData.name;
                city.classList.add('finder-city-row')
                city.addEventListener('click', () => {
                    this.handleCityClick(cityData);
                })
                this._list.appendChild(city);
            })
        }
    }

    handleCityClick(cityData: CityRecord): void {
        // Close any open card when selecting a city
        if (MapService.mapComponent) {
            MapService.mapComponent.hideCurrentCard();
        }
        MapService.panToLocation(cityData.geometry);
        this.resetComponent();
        this._input.value = '';
        this.handleButtonCityFinder();
    }

    onAdd(map: Map): HTMLElement {
        this._map = map;
        this._container.className = this.CLASS_BUTTON_CONTROL + ' mapboxgl-ctrl-group';
        this.mapStyle = this.CLASS_STANDARD;
        this._button.className = `${this.CLASS_BUTTON_CITY_FINDER}`;
        this._button.addEventListener('click', this.handleButtonCityFinder.bind(this));
        this._container.appendChild(this._button);

        document.body.appendChild(this._finderBackground);
        this._finderBackground.classList.add('finder_background');
        this._finderBackground.addEventListener('click', this.handleButtonCityFinder.bind(this));

        this._finderContainer.appendChild(this._input);
        this._finderContainer.classList.add('finder_container')
        this._finderContainer.appendChild(this._list);
        this._list.classList.add('finder_list');
        this._container.appendChild(this._finderContainer);
        this._input.addEventListener('keyup', this.handleInputChange.bind(this));
        
        // Prevent input and finder container clicks from propagating to map container (to avoid closing the card)
        this._input.addEventListener('click', (event) => {
            event.stopPropagation();
        });
        this._input.addEventListener('touchend', (event) => {
            event.stopPropagation();
        });
        this._finderContainer.addEventListener('click', (event) => {
            event.stopPropagation();
        });
        this._finderContainer.addEventListener('touchend', (event) => {
            event.stopPropagation();
        });

        return this._container;
    }

    onRemove(): void {
        this._container.parentNode!.removeChild(this._container);
        this._map = undefined;
    }
}

export default CityFinderControl;
